import React, { useState, useEffect } from 'react';
import { supabase } from '../../../../supabaseClient';
import {
  Box,
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Divider,
  Snackbar,
  Alert,
  Grid,
  CircularProgress
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Archive as ArchiveIcon,
  Notifications as NotificationsIcon,
  Info as InfoIcon,
  Save as SaveIcon,
  Business as BusinessIcon
} from '@mui/icons-material';

const LogBookSettings = ({ user }) => {
  const [settings, setSettings] = useState({
    autoArchive: false,
    archiveDays: 90,
    emailNotifications: true,
    defaultStatus: 'pending',
    requireApproval: false,
    payor: {
      name: 'PROVINCIAL GOVERNMENT OF SURIGAO DEL SUR',
      tin: '000-000-000-000',
      address: 'Capitol Hills, Telaje, Tandag City',
      zip: '8300'
    }
  });

  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Load settings from Supabase (fallback to localStorage)
  useEffect(() => {
    const fetchSettings = async () => {
        try {
            setLoading(true);
            
            // 1. Try fetching from Supabase
            const { data, error } = await supabase
                .from('system_settings')
                .select('value')
                .eq('key', 'logbook_settings')
                .maybeSingle();

            if (error) throw error;

            if (data && data.value) {
                 setSettings(prev => ({
                    ...prev,
                    ...data.value,
                    payor: {
                        ...prev.payor,
                        ...(data.value.payor || {})
                    }
                }));
            } else {
                // 2. Fallback to localStorage if no Supabase entry found
                const storedSettings = localStorage.getItem('logbook_settings');
                if (storedSettings) {
                     const parsedSettings = JSON.parse(storedSettings);
                     setSettings(prev => ({
                        ...prev,
                        ...parsedSettings,
                        payor: {
                            ...prev.payor,
                            ...(parsedSettings.payor || {})
                        }
                    }));
                }
            }
        } catch (err) {
            console.error('Error fetching settings:', err);
            // Fallback to localStorage on error
            const storedSettings = localStorage.getItem('logbook_settings');
             if (storedSettings) {
                try {
                    const parsedSettings = JSON.parse(storedSettings);
                    setSettings(prev => ({
                        ...prev,
                        ...parsedSettings,
                         payor: {
                            ...prev.payor,
                            ...(parsedSettings.payor || {})
                        }
                    }));
                } catch (e) { console.error('LocalStorage parse error', e); }
            }
        } finally {
            setLoading(false);
        }
    };

    fetchSettings();
  }, []);

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    
    try {
        // Check authentication (Optional for logging, but we allow anonymous writes now)
        const { data: { user } } = await supabase.auth.getUser();
        
        // 1. Save to Supabase
        const { error } = await supabase
            .from('system_settings')
            .upsert({ 
                key: 'logbook_settings', 
                value: settings,
                updated_at: new Date().toISOString(),
                updated_by: user ? user.id : null // Allow null if anonymous
            });

        if (error) {
             console.error('Supabase save error:', error);
             // More descriptive error message based on code
             if (error.code === '42501') {
                 setError('Permission denied. You do not have access to save settings to the server.');
             } else {
                 setError('Saved to local storage only. Sync to server failed.');
             }
        }

        // 2. Save to localStorage (as backup/cache)
        localStorage.setItem('logbook_settings', JSON.stringify(settings));
        
        setSaved(true);
    } catch (err) {
        console.error('Error saving settings:', err);
        setError('Saved to local storage only. Error: ' + err.message);
        localStorage.setItem('logbook_settings', JSON.stringify(settings));
        setSaved(true);
    } finally {
        setSaving(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSaved(false);
    setError(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          LogBook Settings
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Configure your e-LogBook system preferences
        </Typography>
      </Box>

      <Paper component="form" onSubmit={handleSaveSettings} sx={{ p: 3 }}>
        {/* General Settings */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <SettingsIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">General Settings</Typography>
          </Box>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Default Status for New Entries
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  The initial status assigned to newly created entries
                </Typography>
                <TextField
                  select
                  fullWidth
                  value={settings.defaultStatus}
                  onChange={(e) => setSettings({ ...settings, defaultStatus: e.target.value })}
                  SelectProps={{ native: true }}
                  size="small"
                >
                  <option value="pending">Pending</option>
                  <option value="in-progress">In Progress</option>
                </TextField>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Require Approval for Entries
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  New entries must be approved before being finalized
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.requireApproval}
                      onChange={(e) => setSettings({ ...settings, requireApproval: e.target.checked })}
                    />
                  }
                  label={settings.requireApproval ? "Enabled" : "Disabled"}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Payor Information Settings */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <BusinessIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">Payor Information</Typography>
          </Box>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Payor Name
                </Typography>
                <TextField
                  fullWidth
                  value={settings.payor?.name || ''}
                  onChange={(e) => setSettings({ ...settings, payor: { ...settings.payor, name: e.target.value } })}
                  size="small"
                  placeholder="e.g. PROVINCIAL GOVERNMENT OF SURIGAO DEL SUR"
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  TIN
                </Typography>
                <TextField
                  fullWidth
                  value={settings.payor?.tin || ''}
                  onChange={(e) => setSettings({ ...settings, payor: { ...settings.payor, tin: e.target.value } })}
                  size="small"
                  placeholder="000-000-000-000"
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Zip Code
                </Typography>
                <TextField
                  fullWidth
                  value={settings.payor?.zip || ''}
                  onChange={(e) => setSettings({ ...settings, payor: { ...settings.payor, zip: e.target.value } })}
                  size="small"
                  placeholder="e.g. 8300"
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Address
                </Typography>
                <TextField
                  fullWidth
                  value={settings.payor?.address || ''}
                  onChange={(e) => setSettings({ ...settings, payor: { ...settings.payor, address: e.target.value } })}
                  size="small"
                  placeholder="e.g. Capitol Hills, Telaje, Tandag City"
                />
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Archiving Settings */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <ArchiveIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">Archiving Settings</Typography>
          </Box>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Auto-Archive Completed Entries
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Automatically archive entries after they are completed
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.autoArchive}
                      onChange={(e) => setSettings({ ...settings, autoArchive: e.target.checked })}
                    />
                  }
                  label={settings.autoArchive ? "Enabled" : "Disabled"}
                />
              </Box>
            </Grid>

            {settings.autoArchive && (
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Archive After (Days)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Number of days to wait before archiving completed entries
                  </Typography>
                  <TextField
                    type="number"
                    fullWidth
                    value={settings.archiveDays}
                    onChange={(e) => setSettings({ ...settings, archiveDays: parseInt(e.target.value) })}
                    inputProps={{ min: 1, max: 365 }}
                    size="small"
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Notification Settings */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <NotificationsIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">Notification Settings</Typography>
          </Box>
          <Divider sx={{ mb: 3 }} />

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Email Notifications
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Receive email updates for important document activities
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.emailNotifications}
                  onChange={(e) => setSettings({ ...settings, emailNotifications: e.target.checked })}
                />
              }
              label={settings.emailNotifications ? "Enabled" : "Disabled"}
            />
          </Box>
        </Box>

        {/* System Information */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <InfoIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">System Information</Typography>
          </Box>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle2" color="text.secondary">System Version</Typography>
              <Typography variant="body1">1.0.0</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle2" color="text.secondary">Last Updated</Typography>
              <Typography variant="body1">{new Date().toLocaleDateString()}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle2" color="text.secondary">Current User</Typography>
              <Typography variant="body1">{user?.firstname} {user?.lastname}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle2" color="text.secondary">User Role</Typography>
              <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>{user?.role}</Typography>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, alignItems: 'center' }}>
          {loading && <Typography variant="caption" color="text.secondary">Loading settings...</Typography>}
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            size="large"
            disabled={saving || loading}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={saved}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={error ? "warning" : "success"} sx={{ width: '100%' }}>
          {error ? error : "Settings saved successfully!"}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LogBookSettings;
